import { createTheme, ThemeProvider, useTheme } from "@mui/material";
import { CollapsibleTable, InstitutionsContext } from "@ngt/opms";
import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ImageUploadUpdaterContext } from "../../contexts/ImageUploadUpdaterContext";
import MriImageUploadsContext from "../../hooks/context/MriImageUploadsContext";
import useRunDicomSignalR from "../../hooks/signalR/useRunDicomSignalR";
import useImageUploadColumns from "./useImageUploadColumns";

interface IImageUploadTableProps {
    canAdministerOpms: boolean;
    isSiteUSer: boolean;
    isCentralReviewer: boolean;
}

const pageSizeOptions = [25, 50, 100]

// const NotUploadCheck = (upload: MriImageUpload) => {
//     return (
//         (upload.mammogramPerformed === true &&
//         !upload.cemDocument?.file) ||
//         (
//         !upload.mriDocument?.file)
//     );
// }
// const NotVerifiedCheck = (upload: MriImageUpload) => {
//     return (
//         (upload.cemDocument?.isDeIdentified === true &&
//         (upload.cemDocument?.isVerified === undefined || 
//         upload.cemDocument?.isVerified === null)) 
//         || 
//         (upload.mriDocument?.isDeIdentified === true &&
//         (upload.mriDocument?.isVerified === undefined || 
//         upload.mriDocument?.isVerified === null))
//     );
// }
// const DeidentifiedFailedCheck = (upload: MriImageUpload) => {
//     return (
//         upload.cemDocument?.deIdentifiedFailed || 
//         upload.mriDocument?.deIdentifiedFailed
//     );
// }

const ImageUploadTable: FC<IImageUploadTableProps> = ({
    canAdministerOpms,
    isSiteUSer,
    isCentralReviewer
}) => {
    const oldTheme = useTheme();

    const theme = createTheme({
        ...oldTheme,
        components: {
            ...oldTheme.components,
            MuiDataGrid: {
                ...oldTheme.components?.MuiDataGrid,
                styleOverrides: {
                    ...oldTheme.components?.MuiDataGrid?.styleOverrides,
                    row: {
                        "&:nth-of-type(even)": {
                            "&:hover": {
                                cursor: "default !important",
                                backgroundColor: oldTheme.palette.mode === 'dark' ? 
                                    `#292929 !important` :
                                    `#eee !important`,
                            },
                        },
                        "&:hover": {
                            cursor: "default !important",
                            backgroundColor: oldTheme.palette.mode === 'dark' ? 
                                `#212121 !important` :
                                `transparent !important`,
                        },
                    }
                },
            },
        },
    });

    const [tableCount, setTableCount] = useState(1);

    const { data: contextImageUploads, loading: contextImageUploadsLoading } = useContext(MriImageUploadsContext);


    const { data: institutions } = useContext(InstitutionsContext);

    const columns = useImageUploadColumns(
        institutions ?? null, 
        canAdministerOpms,
        isSiteUSer,
        isCentralReviewer
    );

    const imageUploadUpdater = useContext(ImageUploadUpdaterContext)
    // time needed because otherwise if the value is the same, the table will not re-render
    const { dicomId, dateTime } = useRunDicomSignalR();

    useEffect(() => {
        if (dicomId) {
            imageUploadUpdater.notify(dicomId);
        }
    }, [dicomId, dateTime]);

    useEffect(() => {
        setTableCount(x => x + 1);
    }, [columns, contextImageUploads]);
    
    const [pageSize, setPageSize] = useState(100);
    const [page, setPage] = useState(0);

    const navigate = useNavigate();
    const { institutionCode } = useParams();

    const rows = useMemo(() => {
        if (!contextImageUploads) return [];
        const seen = new Set<number>();
        return contextImageUploads.filter(u => {
            if (u.patientId == null || seen.has(u.patientId)) return false;
            seen.add(u.patientId);
            return true;
        });
    }, [contextImageUploads]);

    const onRowClick = useCallback((params: any) => {
        const patientId = params.row?.patientId;
        if (patientId == null) {
            return;
        };
        const base = institutionCode ? `/image-upload/${institutionCode}` : `/image-upload`;
        console.log('Navigating to patient with id:', patientId);
        
        navigate(`${base}/patient/${patientId}`);
    }, [navigate, institutionCode]);

    return (
        <>
            <ThemeProvider theme={theme}>
                <CollapsibleTable
                    key={tableCount}
                    rows={rows}
                    loading={contextImageUploadsLoading}
                    columns={columns as any}
                    //options={tableOptions}
                    pageSizeOptions={pageSizeOptions}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 100 }},
                    }}
                    title={"Participant Image Uploads"}
                    pluralizeTitle={false}
                    entityName={"Participant"}
                    disabledTableText={"There are no participants eligible for uploads."}
                    startCollapsed={false}
                    paginationModel={{ page: page, pageSize: pageSize }}
                    onPaginationModelChange={(paginationModel) => {
                        setPage(paginationModel.page);
                        setPageSize(paginationModel.pageSize);
                    }}
                    onRowClick={onRowClick}
                    sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
                />
            </ThemeProvider>
        </>

    );
}

export default ImageUploadTable;